import { GQL_ID } from '@@partner:types:graphql'

import React, { useState } from 'react'
import { Panel, Button } from 'rsuite'
import {
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import SortIcon from '@rsuite/icons/Sort'
import { CloseCircleOutline, EyeOffOutline, EyeOutline } from 'react-ionicons'

import { MainPageBlockForm } from '../form/MainPageConfigFormState'
import { ActiveEditorState } from 'containers/marketing/mainPageConfig/MainPageConfigFormPage'

import BannerBlock from './BannerBlock'
import NewBlock from './NewBlock'
import BannerGalleryBlock from './BannerGalleryBlock'
import ProductsBlock from './ProductsBlock'
import TextMaterialBlock from './TextMaterialBlock'
import CategoryBlock from './CategoryBlock'
import MenuBlock from './MenuBlock'
import PersonalOffersBlock from './PersonalOffersBlock'
import CouponsBlock from './CouponsBlock'
import LoyaltyLevelBlock from './LoyaltyLevelBlock'
import CustomBlock from './CustomBlock'

type EditorItemProps = {
  block: MainPageBlockForm
  onChange: (block: MainPageBlockForm) => void
  onDelete: (blockId: GQL_ID) => void
  onOpenEditor?: (editor: ActiveEditorState) => void
}

interface BlockType {
  name: string
  type: string | null
  subtypes?: {
    label: string
    value: string
  }[]
}

export const blockTypes: BlockType[] = [ 
  { 
    name: 'Баннер',
    type: 'banner', 
    subtypes: [] 
  }, 
  { 
    name: 'Галерея баннеров',
    type: 'bannerGallery', 
    subtypes: [ 
      {
        label: 'Большой',
        value: 'big'
      },
      { 
        label: 'Маленький',
        value: 'small'
      }
    ] 
  },
  { 
    name: 'Товары',
    type: 'products', 
    subtypes: [ 
      {
        label: 'Группа',
        value: 'group'
      },
      {
        label: 'Категория',
        value: 'category'
      },
      {
        label: 'Заказывали ранее',
        value: 'previouslyOrdered'
      },
      {
        label: 'Новинки',
        value: 'new'
      },
      {
        label: 'Популярные',
        value: 'popular'
      },
      {
        label: 'Распродажа',
        value: 'sale'
      },
      {
        label: 'Персонализированные',
        value: 'personalized'
      },
      {
        label: 'По фильтру',
        value: 'filter'
      }
    ] 
  },
  { 
    name: 'Тексты',
    type: 'textMaterials', 
    subtypes: [ 
      {
        label: 'Одиночный',
        value: 'single'
      },
      {
        label: 'Блок',
        value: 'block'
      }
    ] 
  },
  {
    name: 'Категории',
    type: 'categories',
  },
  {
    name: 'Меню',
    type: 'menu',
  },
  {
    name: 'Персональные предложения',
    type: 'personalOffers',
  },
  {
    name: 'Купоны',
    type: 'coupons',
  },
  {
    name: 'Уровень программы лояльности',
    type: 'loyaltyLevel',
  },
  {
    name: 'Кастомный блок',
    type: 'custom',
  },
  {
    name: 'Новый блок',
    type: null,
  }
]

function MapBlockTypeToComponent (
  block: MainPageBlockForm, 
  onChange: (block: MainPageBlockForm) => void,
  onOpenEditor?: (editor: ActiveEditorState) => void
) {
  switch (block.type) {
  case null:
    return <NewBlock block={block} onChange={onChange} />
  case 'banner':
    return <BannerBlock block={block} onChange={onChange} />
  case 'bannerGallery':
    return <BannerGalleryBlock block={block} onChange={onChange} onOpenEditor={onOpenEditor} />
  case 'products':
    return <ProductsBlock block={block} onChange={onChange} />
  case 'textMaterials':
    return <TextMaterialBlock block={block} onChange={onChange} />
  case 'categories':
    return <CategoryBlock block={block} onChange={onChange} />
  case 'menu':
    return <MenuBlock block={block} onChange={onChange} onOpenEditor={onOpenEditor} />
  case 'personalOffers':
    return <PersonalOffersBlock block={block} onChange={onChange} />
  case 'coupons':
    return <CouponsBlock block={block} onChange={onChange} />
  case 'loyaltyLevel':
    return <LoyaltyLevelBlock block={block} onChange={onChange} />
  case 'custom':
    return <CustomBlock block={block} onChange={onChange} />
  default:
    return <div></div>
  }
}

export default function EditorItem ({
  block,
  onChange,
  onDelete,
  onOpenEditor
}: EditorItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: block.id,
  })

  const [ showConfirm, setShowConfirm ] = useState(false)

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    marginBottom: 10,
    opacity: block.hidden ? 0.5 : 1
  }

  const handleShowConfirm = () => {
    setShowConfirm(true)
  }

  const handleDelete = () => {
    onDelete(block.id)
    setShowConfirm(false)
  }

  const handleHideClick = () => {
    onChange({
      ...block,
      hidden: !block.hidden
    })
  }

  return (
    <>
      <Panel 
        ref={setNodeRef} 
        bordered 
        shaded 
        style={style}
      >
        <div style={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div
            {...attributes}
            {...listeners}
            style={{
              cursor: 'grab',
              marginBottom: 10,
              fontWeight: 'bold',
              color: '#555',
            }}
          >
            <div>
              <SortIcon style={{ fontSize: 16 }} />
              { blockTypes.find(bt => bt.type === block.type)?.name }
              { block.type === 'custom' && block.subtype && ` (${block.subtype})` }
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
            
            { !block.hidden ? (
              <EyeOutline
                onClick={handleHideClick}
                style={{
                  cursor: 'pointer',
                  marginRight: 10,
                }}
              /> ) : (
              <EyeOffOutline
                onClick={handleHideClick}
                style={{
                  cursor: 'pointer',
                  marginRight: 10,
                }}
              />
            )}
            { showConfirm ? (
              <div>
                <Button 
                  id='delete-confirm'
                  appearance='link' 
                  color="red" 
                  onClick={handleDelete}
                >
                  Удалить
                </Button>
                <Button 
                  id='delete-cancel'
                  appearance='link' 
                  color="blue" 
                  onClick={() => setShowConfirm(false)}
                >
                  Отменить
                </Button>
              </div>
            ) : (
              <CloseCircleOutline
                style={{
                  cursor: 'pointer',
                }}
                onClick={handleShowConfirm} 
              />
            )}
          </div>
        </div>

        <div style={{ marginTop: 10 }}>
          { MapBlockTypeToComponent(block, onChange, onOpenEditor) }
        </div>

        
      </Panel>
    </>
  )
}